import { useCallback, useEffect, useMemo, useState } from 'react'
import { FilterBar } from '../components/common/FilterBar'
import { ShieldAlert, AlertCircle, TriangleAlert, User, CalendarClock, FileText, UserCheck } from 'lucide-react'
import { useGetVehicleIncidentCards, useGetVehicleIncidents } from '../hooks/backend/fleet'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { IncidentDetailDialog } from '../components/fleet/IncidentDetailDialog'
import { IncidentFormDialog } from '../components/fleet/IncidentFormDialog'
import { IncidentCardDialog } from '../components/fleet/IncidentCardDialog'
import { type IncidentCard, onlineLabel } from '../components/fleet/incidentTimeline'
import { formatDate, type FleetRow } from '../utils/fleet'
import {
  INCIDENT_TYPES, type IncidentRow, incidentStatusLabel, incidentStatusTone,
  incidentTypeLabel, incidentTypeTone, updateStateChip, zar,
} from '../utils/servicing'

type TypeTab = 'all' | 'open' | string

const OPEN_STATUSES = ['open', 'investigating', 'escalated']

/**
 * Vehicle incidents, one card per car.
 *
 * This page is the reason the Typeform stopped being a dead end. Between 19
 * March and 21 August 2026, 210 incident reports were filed and none of them
 * reached the database: they lived in a Google Sheet and a Gmail thread.
 *
 * ONE CARD PER CAR, NOT ONE ROW PER INCIDENT (migration 1601, 1 October 2026).
 * The list had 295 rows for 39 cars, MF17LKGP alone 19 of them and 13 open,
 * because every repair note had been logged as a new incident. A card is the car:
 * online or offline, its whole incident history, and the incident that is open.
 * A new incident is still its own record and still goes to the owner as its own
 * report; it lands on the car's card.
 *
 * Everything on a card is computed in v_vehicle_incident_cards. The open incident
 * rows are loaded as well only so the dialogs on a card have the incident they
 * edit.
 */
export default function VehicleIncidents() {
  const { loading: accessLoading, canView, canEditFleet, caption } = useCapabilities()
  const [tab, setTab] = useState<TypeTab>('open')
  const [search, setSearch] = useState('')

  const cardsFeed = useGetVehicleIncidentCards()
  const rowsFeed = useGetVehicleIncidents()
  const cardsTrigger = cardsFeed.trigger
  const rowsTrigger = rowsFeed.trigger

  const load = useCallback(() => {
    cardsTrigger({}, { skipCache: true })
    rowsTrigger({ statuses: OPEN_STATUSES }, { skipCache: true })
  }, [cardsTrigger, rowsTrigger])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const allCards = (cardsFeed.data as IncidentCard[] | undefined) ?? []
  const openRows = (rowsFeed.data as IncidentRow[] | undefined) ?? []

  const openByCar = useMemo(() => {
    const m = new Map<string, IncidentRow[]>()
    for (const r of openRows) {
      if (!r.vehicle_id) continue
      m.set(r.vehicle_id, [...(m.get(r.vehicle_id) ?? []), r])
    }
    return m
  }, [openRows])

  const cards = useMemo(() => {
    const term = search.trim().toLowerCase()
    return allCards.filter((c) => {
      if (tab === 'open' && c.open_count === 0) return false
      if (tab !== 'open' && tab !== 'all' && !(c.incident_types ?? []).includes(tab)) return false
      return !term || (c.search_text ?? '').toLowerCase().includes(term)
    })
  }, [allCards, tab, search])

  const counts = useMemo(() => {
    const open = allCards.filter((c) => c.open_count > 0)
    const byType: Record<string, number> = {}
    for (const c of allCards) for (const t of c.incident_types ?? []) byType[t] = (byType[t] ?? 0) + 1
    return {
      byType,
      openCars: open.length,
      needsUpdate: open.filter((c) => c.update_state === 'update_overdue' || c.update_state === 'no_update_date').length,
      // An open incident nobody is in charge of cannot raise a R50 when its
      // update is missed, so it is the one state the rule cannot catch.
      noOneInCharge: open.filter((c) => c.nobody_in_charge).length,
      offline: open.filter((c) => !c.online).length,
      online: open.filter((c) => c.online).length,
    }
  }, [allCards])

  const TABS: { value: TypeTab; label: string; count?: number }[] = [
    { value: 'open', label: 'Open', count: counts.openCars },
    { value: 'all', label: 'All cars', count: allCards.length },
    ...INCIDENT_TYPES.map((t) => ({ value: t.code as TypeTab, label: t.label, count: counts.byType[t.code] ?? 0 })),
  ]

  const loading = cardsFeed.loading
  const error = cardsFeed.error

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Vehicle incidents are available to admins only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <TriangleAlert className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Vehicle incidents</h1>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-muted-foreground tabular-nums">
              {loading ? '…' : `${cards.length} car${cards.length === 1 ? '' : 's'}`}
            </span>
            {canEditFleet && (
              <IncidentFormDialog
                onSaved={load}
                triggerLabel="Log repair / incident"
                triggerSize="default"
              />
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>

        <div className="mt-3 inline-flex max-w-full flex-wrap rounded-md border border-border bg-background p-0.5">
          {TABS.map((t) => {
            const active = tab === t.value
            return (
              <button key={String(t.value)} type="button" onClick={() => setTab(t.value)}
                className={cn(
                  'flex items-center gap-1.5 rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
                  active
                    ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                    : 'text-muted-foreground hover:text-foreground',
                )}>
                {t.label}
                {t.count !== undefined && <span className="tabular-nums opacity-70">{t.count}</span>}
              </button>
            )
          })}
        </div>

        <div className="mt-2.5 flex flex-wrap gap-2">
          <Chip tone={counts.needsUpdate > 0 ? 'red' : 'green'}>
            Needs an update
            <span className="ml-1 font-semibold tabular-nums">{counts.needsUpdate}</span>
          </Chip>
          <Chip tone={counts.noOneInCharge > 0 ? 'red' : 'green'}>
            Nobody in charge
            <span className="ml-1 font-semibold tabular-nums">{counts.noOneInCharge}</span>
          </Chip>
          <Chip tone={counts.offline > 0 ? 'amber' : 'green'}>
            Open and offline
            <span className="ml-1 font-semibold tabular-nums">{counts.offline}</span>
          </Chip>
          <Chip tone="neutral">
            Open and online
            <span className="ml-1 font-semibold tabular-nums">{counts.online}</span>
          </Chip>
        </div>
      </div>

      <FilterBar
        activeCount={search.trim() ? 1 : 0}
        summary={search.trim() ? `"${search.trim()}"` : ''}
        trailing={`${cards.length} cars`}
      >
        <div className="flex flex-wrap items-center gap-2 p-3">
          <Input className="min-w-[240px] flex-1" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search registration, owner, driver, description or case number" />
        </div>
      </FilterBar>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <div className="m-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : loading && cards.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading incidents…
          </div>
        ) : cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <FileText className="h-6 w-6" />
            No cars match these filters.
          </div>
        ) : (
          <ul>
            {cards.map((c) => (
              <CardRow
                key={c.vehicle_id}
                card={c}
                openIncidents={openByCar.get(c.vehicle_id) ?? []}
                canEditFleet={canEditFleet}
                onSaved={load}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function CardRow({
  card: c, openIncidents, canEditFleet, onSaved,
}: { card: IncidentCard; openIncidents: IncidentRow[]; canEditFleet: boolean; onSaved: () => void }) {
  const state = updateStateChip(c.update_state, null)
  const urgent = c.update_state === 'update_overdue'
  const current = openIncidents[0] ?? null
  // The incident form takes a fleet row when the vehicle is fixed; these are the
  // only fields it reads from one.
  const vehicle = {
    vehicle_id: c.vehicle_id, registration_number: c.registration_number, make: c.make,
    model: c.model, operational: c.operational, location: c.current_location,
  } as unknown as FleetRow

  return (
    <li>
      <div className={cn(
        'flex flex-col gap-2 border-b border-border px-3 py-3 sm:flex-row sm:items-start',
        urgent && 'bg-rose-50/50 dark:bg-rose-950/20',
      )}>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-bold uppercase tracking-wide text-foreground">
              {c.registration_number || 'No registration'}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {[c.make, c.model].filter(Boolean).join(' ')}
            </span>
            <Chip tone={c.online ? 'green' : 'red'} className="whitespace-normal">{onlineLabel(c)}</Chip>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {c.owner_name && (
              <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{c.owner_name}</span>
            )}
            <span>
              {c.incidents_total} incident{c.incidents_total === 1 ? '' : 's'},{' '}
              {c.open_count === 0 ? 'none open' : `${c.open_count} open`}
            </span>
            {c.open_count > 0 && c.next_update_date && (
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="h-3 w-3" />Next update by {formatDate(c.next_update_date)}
              </span>
            )}
            {c.open_count > 0 && (
              <span className={cn('inline-flex items-center gap-1', c.nobody_in_charge && 'font-medium text-destructive')}>
                <UserCheck className="h-3 w-3" />
                {c.nobody_in_charge ? 'Nobody in charge' : `In charge: ${c.in_charge_name ?? 'not known'}`}
              </span>
            )}
          </div>

          {c.latest_title && (
            <p className="mt-1.5 text-xs text-foreground">
              <span className="font-medium">{c.open_count > 0 ? 'Open' : 'Latest'}: {c.latest_title}</span>
              {c.latest_incident_at ? `, ${formatDate(c.latest_incident_at)}` : ''}
              {c.latest_description ? `. ${c.latest_description}` : ''}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:flex-col sm:items-end">
          {c.latest_type && <Chip tone={incidentTypeTone(c.latest_type)}>{incidentTypeLabel(c.latest_type)}</Chip>}
          {c.latest_status && <Chip tone={incidentStatusTone(c.latest_status)}>{incidentStatusLabel(c.latest_status)}</Chip>}
          {c.update_state !== 'closed' && <Chip tone={state.tone}>{state.label}</Chip>}
          {Number(c.offerings_owed_zar ?? 0) > 0 && (
            <Chip tone="red">{zar(Number(c.offerings_owed_zar))} Sin Offering</Chip>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            {current && <IncidentDetailDialog row={current} canEdit={canEditFleet} onSaved={onSaved} />}
            <IncidentCardDialog card={c} openIncidents={openIncidents} canEdit={canEditFleet} onSaved={onSaved} />
            {canEditFleet && (
              <IncidentFormDialog vehicle={vehicle} onSaved={onSaved} triggerLabel="New incident" />
            )}
          </div>
        </div>
      </div>
    </li>
  )
}
